/* =====================================================================
   Jade — comportamento do tema, em JavaScript puro (custom elements).
   Configuração vem de window.theme, definido em layout/theme.liquid.
   ===================================================================== */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const strings = theme.strings || {};

  /* ---------- Utilidades ---------- */

  function formatMoney(cents, format) {
    const fmt = format || theme.moneyFormat || '${{amount}}';
    const placeholder = /\{\{\s*(\w+)\s*\}\}/;
    const value = typeof cents === 'string' ? Number(cents.replace('.', '')) : Number(cents);

    const withDelimiters = (number, precision, thousands, decimal) => {
      const fixed = (number / 100).toFixed(precision);
      const [whole, fraction] = fixed.split('.');
      const grouped = whole.replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return fraction ? grouped + decimal + fraction : grouped;
    };

    const match = fmt.match(placeholder);
    if (!match) return fmt;
    let amount;
    switch (match[1]) {
      case 'amount_no_decimals':
        amount = withDelimiters(value, 0, ',', '.');
        break;
      case 'amount_with_comma_separator':
        amount = withDelimiters(value, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        amount = withDelimiters(value, 0, '.', ',');
        break;
      case 'amount_with_apostrophe_separator':
        amount = withDelimiters(value, 2, "'", '.');
        break;
      case 'amount_with_space_separator':
        amount = withDelimiters(value, 2, ' ', ',');
        break;
      default:
        amount = withDelimiters(value, 2, ',', '.');
    }
    return fmt.replace(placeholder, amount);
  }

  /* Mesma conta de snippets/installments.liquid. Retorna HTML ou '' se não couber parcela. */
  function installmentText(priceCents) {
    const cfg = theme.installments || {};
    if (!cfg.enabled) return '';
    let count = Number(cfg.max) || 12;
    const rate = (Number(cfg.rate) || 0) / 100;
    const minCents = (Number(cfg.minValue) || 0) * 100;
    if (minCents > 0) count = Math.min(count, Math.floor(priceCents / minCents));
    if (count < 2) return '';
    let parcel;
    if (rate > 0) {
      const growth = Math.pow(1 + rate, count);
      parcel = Math.round((priceCents * rate * growth) / (growth - 1));
    } else {
      parcel = Math.round(priceCents / count);
    }
    const free = rate === 0 && cfg.freeLabel ? ` ${strings.interestFree}` : '';
    return `${strings.upTo} <strong>${count}x</strong> ${strings.of} <strong>${formatMoney(parcel)}</strong>${free}`;
  }

  const debounce = (fn, wait = 300) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const parseHTML = (html) => new DOMParser().parseFromString(html, 'text/html');

  /* ---------- Gavetas e sobreposição ---------- */

  const overlay = document.querySelector('[data-overlay]');
  let openDrawerEl = null;
  let lastFocus = null;

  function openDrawer(drawer) {
    if (!drawer) return;
    if (openDrawerEl && openDrawerEl !== drawer) closeDrawer(false);
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    if (overlay) overlay.hidden = false;
    document.body.classList.add('is-locked');
    openDrawerEl = drawer;
    const focusTarget = drawer.querySelector('[data-drawer-close], button, a, input');
    if (focusTarget) setTimeout(() => focusTarget.focus(), 50);
  }

  function closeDrawer(restoreFocus = true) {
    if (!openDrawerEl) return;
    openDrawerEl.classList.remove('is-open');
    openDrawerEl.setAttribute('aria-hidden', 'true');
    if (overlay) overlay.hidden = true;
    document.body.classList.remove('is-locked');
    openDrawerEl = null;
    if (restoreFocus && lastFocus) lastFocus.focus();
  }

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (opener) {
      const drawer = document.getElementById(opener.dataset.drawerOpen);
      if (drawer) {
        event.preventDefault();
        openDrawer(drawer);
      }
      return;
    }
    if (event.target.closest('[data-drawer-close]') || event.target === overlay) {
      closeDrawer();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeDrawer();
  });

  /* ---------- Diálogos ---------- */

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (opener) {
      const modal = document.getElementById(opener.dataset.modalOpen);
      if (modal && typeof modal.showModal === 'function') {
        event.preventDefault();
        modal.showModal();
      }
      return;
    }
    const closer = event.target.closest('[data-modal-close]');
    if (closer) {
      closer.closest('dialog')?.close();
      return;
    }
    if (event.target.tagName === 'DIALOG') event.target.close();
  });

  /* ---------- Barra de avisos ---------- */

  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.slides = [...this.querySelectorAll('.announcement__slide')];
      if (this.slides.length < 2) return;
      this.index = 0;
      this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(-1, true));
      this.querySelector('[data-next]')?.addEventListener('click', () => this.go(1, true));
      this.delay = Number(this.dataset.autoplay || 0) * 1000;
      this.start();
      this.addEventListener('mouseenter', () => this.stop());
      this.addEventListener('mouseleave', () => this.start());
    }

    go(step, manual) {
      this.slides[this.index].classList.remove('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'true');
      this.index = (this.index + step + this.slides.length) % this.slides.length;
      this.slides[this.index].classList.add('is-active');
      this.slides[this.index].setAttribute('aria-hidden', 'false');
      if (manual) this.start();
    }

    start() {
      this.stop();
      if (this.delay > 0) this.timer = setInterval(() => this.go(1), this.delay);
    }

    stop() {
      clearInterval(this.timer);
    }
  }
  customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Cabeçalho fixo ---------- */

  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.header = this.querySelector('.header');
      const onScroll = () => this.header?.classList.toggle('is-scrolled', window.scrollY > 10);
      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();
      this.querySelector('[data-search-toggle]')?.addEventListener('click', () => {
        const box = this.querySelector('[data-mobile-search]');
        if (!box) return;
        box.hidden = !box.hidden;
        if (!box.hidden) box.querySelector('input')?.focus();
      });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Busca com sugestões ---------- */

  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input[type="search"]');
      this.results = this.querySelector('[data-predictive-results]');
      if (!this.input || !this.results || this.dataset.enabled !== 'true') return;
      this.input.addEventListener('input', debounce(() => this.search(), 300));
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim()) this.results.hidden = false;
      });
      document.addEventListener('click', (event) => {
        if (!this.contains(event.target)) this.results.hidden = true;
      });
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          this.results.hidden = true;
          this.input.focus();
        }
      });
    }

    async search() {
      const query = this.input.value.trim();
      if (!query) {
        this.results.hidden = true;
        return;
      }
      if (this.controller) this.controller.abort();
      this.controller = new AbortController();
      try {
        const url = `${routes.predictiveSearch}?q=${encodeURIComponent(query)}&section_id=predictive-search&resources[type]=product&resources[limit]=6`;
        const response = await fetch(url, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const html = parseHTML(await response.text());
        const content = html.querySelector('[data-predictive-content]');
        this.results.innerHTML = content ? content.innerHTML : '';
        this.results.hidden = false;
      } catch (error) {
        if (error.name !== 'AbortError') this.results.hidden = true;
      }
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Carrossel ---------- */

  class SliderComponent extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.slider');
      if (!this.track) return;
      this.slides = [...this.track.children];
      this.prev = this.querySelector('.slider-button--prev');
      this.next = this.querySelector('.slider-button--next');
      this.dots = [...this.querySelectorAll('.slider-dot')];
      this.loop = this.dataset.loop === 'true';

      this.prev?.addEventListener('click', () => this.step(-1));
      this.next?.addEventListener('click', () => this.step(1));
      this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.goTo(i)));
      this.track.addEventListener('scroll', debounce(() => this.update(), 60), { passive: true });
      window.addEventListener('resize', debounce(() => this.update(), 150));
      this.update();

      const seconds = Number(this.dataset.autoplay || 0);
      if (seconds > 0 && this.slides.length > 1) {
        this.delay = seconds * 1000;
        this.play();
        this.addEventListener('mouseenter', () => this.pause());
        this.addEventListener('mouseleave', () => this.play());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('touchstart', () => this.pause(), { passive: true });
      }
    }

    get current() {
      const left = this.track.scrollLeft;
      let best = 0;
      let bestDistance = Infinity;
      this.slides.forEach((slide, i) => {
        const distance = Math.abs(slide.offsetLeft - this.track.offsetLeft - left);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      });
      return best;
    }

    goTo(index, smooth = true) {
      const slide = this.slides[index];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft - this.track.offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
    }

    step(direction) {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (this.loop && direction > 0 && this.track.scrollLeft >= max) {
        this.track.scrollTo({ left: 0, behavior: 'smooth' });
        return;
      }
      if (this.loop && direction < 0 && this.track.scrollLeft <= 2) {
        this.track.scrollTo({ left: this.track.scrollWidth, behavior: 'smooth' });
        return;
      }
      this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: 'smooth' });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth - 2;
      if (!this.loop) {
        if (this.prev) this.prev.disabled = this.track.scrollLeft <= 2;
        if (this.next) this.next.disabled = this.track.scrollLeft >= max;
      }
      const index = this.current;
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === index ? 'true' : 'false'));
      if (index !== this.lastIndex) {
        this.lastIndex = index;
        this.dispatchEvent(new CustomEvent('slider:change', { detail: { index }, bubbles: true }));
      }
    }

    play() {
      this.pause();
      this.timer = setInterval(() => this.step(1), this.delay);
    }

    pause() {
      clearInterval(this.timer);
    }
  }
  customElements.define('slider-component', SliderComponent);

  /* ---------- Quantidade ---------- */

  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelector('[data-quantity-minus]')?.addEventListener('click', () => this.change(-1));
      this.querySelector('[data-quantity-plus]')?.addEventListener('click', () => this.change(1));
    }

    change(step) {
      const min = Number(this.input.min || 0);
      const max = this.input.max ? Number(this.input.max) : Infinity;
      const value = Math.min(max, Math.max(min, (Number(this.input.value) || 0) + step));
      if (value === Number(this.input.value)) return;
      this.input.value = value;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Carrinho ---------- */

  function updateCartCount(count) {
    document.querySelectorAll('[data-cart-count]:not(cart-drawer)').forEach((el) => {
      el.textContent = count;
    });
  }

  function renderCartSections(sections) {
    if (!sections) return;
    const drawerHTML = sections['cart-drawer'];
    if (drawerHTML) {
      const fresh = parseHTML(drawerHTML).querySelector('cart-drawer');
      const current = document.querySelector('cart-drawer');
      if (fresh && current) {
        current.innerHTML = fresh.innerHTML;
        updateCartCount(fresh.dataset.cartCount);
      }
    }
    const page = document.querySelector('cart-page');
    if (page && sections[page.dataset.sectionId]) {
      const fresh = parseHTML(sections[page.dataset.sectionId]).querySelector('cart-page');
      if (fresh) page.innerHTML = fresh.innerHTML;
    }
  }

  function cartSectionIds() {
    const ids = ['cart-drawer'];
    const page = document.querySelector('cart-page');
    if (page) ids.push(page.dataset.sectionId);
    return ids;
  }

  async function changeLine(line, quantity) {
    const containers = document.querySelectorAll('cart-drawer, cart-page');
    containers.forEach((el) => el.querySelector('.cart-items')?.classList.add('is-updating'));
    try {
      const response = await fetch(`${routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity, sections: cartSectionIds(), sections_url: window.location.pathname })
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || strings.cartError);
      renderCartSections(data.sections);
    } catch (error) {
      window.alert(error.message || strings.cartError);
      containers.forEach((el) => el.querySelector('.cart-items')?.classList.remove('is-updating'));
    }
  }

  class CartArea extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', (event) => {
        const input = event.target.closest('[data-cart-quantity]');
        if (input) changeLine(Number(input.dataset.line), Number(input.value));
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-cart-remove]');
        if (remove) {
          event.preventDefault();
          changeLine(Number(remove.dataset.cartRemove), 0);
          return;
        }
        // Finalizar compra na gaveta: vai para o checkout da Yampi, como o
        // resto da loja. O snippet da Yampi so cuida da pagina /cart, entao
        // a gaveta chama a mesma funcao dele. Sem Yampi, cai na pagina do
        // carrinho, onde o fluxo padrao continua.
        const checkout = event.target.closest('button[name="checkout"]');
        if (checkout && this.tagName === 'CART-DRAWER') {
          event.preventDefault();
          checkout.classList.add('is-loading');
          if (typeof window.getNewCheckoutURL === 'function') {
            window.getNewCheckoutURL();
          } else if (typeof window.yampiClick === 'function') {
            window.yampiClick();
          } else {
            window.location.href = routes.cart;
          }
        }
      });
    }
  }
  customElements.define('cart-drawer', class extends CartArea {});
  customElements.define('cart-page', class extends CartArea {});

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.form.addEventListener('submit', (event) => this.onSubmit(event));
    }

    async onSubmit(event) {
      event.preventDefault();
      const button = event.submitter || this.form.querySelector('[type="submit"]');
      const errorBox = this.querySelector('[data-form-error]');
      if (button?.getAttribute('aria-disabled') === 'true') return;
      button?.classList.add('is-loading');
      if (errorBox) errorBox.hidden = true;

      const body = new FormData(this.form);
      const goToCheckout = button?.name === 'checkout';
      const usePage = theme.cartType === 'page' || !document.querySelector('cart-drawer');
      if (!goToCheckout && !usePage) {
        body.append('sections', cartSectionIds().join(','));
        body.append('sections_url', window.location.pathname);
      }

      try {
        const response = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || strings.cartError);

        if (goToCheckout) {
          window.location.href = '/checkout';
          return;
        }
        if (usePage) {
          window.location.href = routes.cart;
          return;
        }
        renderCartSections(data.sections);
        openDrawer(document.querySelector('cart-drawer'));
      } catch (error) {
        if (errorBox) {
          errorBox.textContent = error.message;
          errorBox.hidden = false;
        } else {
          window.alert(error.message);
        }
      } finally {
        button?.classList.remove('is-loading');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Favoritos ---------- */

  const WISHLIST_KEY = 'jade:wishlist';
  const wishlist = {
    get() {
      try {
        return JSON.parse(localStorage.getItem(WISHLIST_KEY)) || [];
      } catch (error) {
        return [];
      }
    },
    set(list) {
      localStorage.setItem(WISHLIST_KEY, JSON.stringify(list));
      this.sync();
    },
    toggle(handle) {
      const list = this.get();
      const index = list.indexOf(handle);
      if (index > -1) list.splice(index, 1);
      else list.unshift(handle);
      this.set(list);
    },
    sync() {
      const list = this.get();
      document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
        button.setAttribute('aria-pressed', list.includes(button.dataset.wishlistToggle) ? 'true' : 'false');
      });
      document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
        el.textContent = list.length;
      });
    }
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-wishlist-toggle]');
    if (!button) return;
    event.preventDefault();
    wishlist.toggle(button.dataset.wishlistToggle);
    const page = document.querySelector('wishlist-page');
    if (page && button.getAttribute('aria-pressed') === 'false') page.render();
  });

  class WishlistPage extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-wishlist-grid]');
      this.empty = this.querySelector('[data-wishlist-empty]');
      this.render();
    }

    async render() {
      const handles = wishlist.get();
      if (!handles.length) {
        this.grid.innerHTML = '';
        this.empty.hidden = false;
        return;
      }
      this.empty.hidden = true;
      const cards = await Promise.all(
        handles.map(async (handle) => {
          try {
            const response = await fetch(`${routes.root}products/${encodeURIComponent(handle)}?section_id=wishlist-card`);
            if (!response.ok) return '';
            const card = parseHTML(await response.text()).querySelector('.product-card');
            return card ? `<div>${card.outerHTML}</div>` : '';
          } catch (error) {
            return '';
          }
        })
      );
      this.grid.innerHTML = cards.join('');
      if (!this.grid.innerHTML.trim()) this.empty.hidden = false;
      wishlist.sync();
    }
  }
  customElements.define('wishlist-page', WishlistPage);

  /* ---------- Antes e depois ---------- */

  class ImageCompare extends HTMLElement {
    connectedCallback() {
      const range = this.querySelector('input[type="range"]');
      if (!range) return;
      const set = () => this.style.setProperty('--position', `${range.value}%`);
      range.addEventListener('input', set);
      set();
    }
  }
  customElements.define('image-compare', ImageCompare);

  /* ---------- Contadores ---------- */

  class CounterUp extends HTMLElement {
    connectedCallback() {
      this.target = Number(this.dataset.target || 0);
      this.textContent = '0';
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          this.animate();
        }
      });
      observer.observe(this);
    }

    animate() {
      const duration = Number(this.dataset.duration || 2000);
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduce) {
        this.textContent = this.target.toLocaleString('pt-BR');
        return;
      }
      const start = performance.now();
      const tick = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        this.textContent = Math.round(this.target * eased).toLocaleString('pt-BR');
        if (progress < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
  }
  customElements.define('counter-up', CounterUp);

  /* ---------- Página de produto ---------- */

  class ProductInfo extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-product-json]');
      if (!json) return;
      this.product = JSON.parse(json.textContent);
      this.idInput = this.querySelector('form[action*="/cart/add"] input[name="id"]');
      this.addEventListener('change', (event) => {
        if (event.target.closest('[data-option-index]')) this.onOptionChange();
      });
      this.setupGallery();
      this.markUnavailable();
    }

    selectedOptions() {
      return [...this.querySelectorAll('[data-option-index]')].map((group) => {
        const checked = group.querySelector('input:checked');
        if (checked) return checked.value;
        return group.querySelector('select')?.value;
      });
    }

    onOptionChange() {
      const options = this.selectedOptions();
      const variant = this.product.variants.find((v) => v.options.every((value, i) => value === options[i]));
      this.querySelectorAll('[data-option-index]').forEach((group, i) => {
        const label = group.querySelector('[data-option-value]');
        if (label) label.textContent = options[i];
      });
      this.markUnavailable();
      this.updateButton(variant);
      if (!variant) return;

      if (this.idInput) {
        this.idInput.value = variant.id;
        this.idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updatePrice(variant);

      const sku = this.querySelector('[data-sku]');
      if (sku) {
        sku.textContent = variant.sku || '';
        sku.closest('[data-sku-wrapper]')?.toggleAttribute('hidden', !variant.sku);
      }

      if (variant.featured_media) this.showMedia(variant.featured_media.id);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    updatePrice(variant) {
      const current = this.querySelector('[data-price-current]');
      const compare = this.querySelector('[data-price-compare]');
      const priceBox = this.querySelector('[data-price]');
      if (current) current.textContent = formatMoney(variant.price);
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      if (compare) {
        compare.textContent = onSale ? formatMoney(variant.compare_at_price) : '';
        compare.hidden = !onSale;
      }
      priceBox?.classList.toggle('price--on-sale', Boolean(onSale));
      const installments = this.querySelector('[data-installments]');
      if (installments) {
        const text = installmentText(variant.price);
        installments.hidden = !text;
        installments.querySelector('[data-installments-text]').innerHTML = text;
      }
    }

    updateButton(variant) {
      const button = this.querySelector('[data-add-button]');
      if (!button) return;
      const label = button.querySelector('[data-add-label]') || button;
      if (!variant) {
        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
        label.textContent = strings.unavailable;
      } else if (!variant.available) {
        button.setAttribute('aria-disabled', 'true');
        button.disabled = true;
        label.textContent = strings.soldOut;
      } else {
        button.removeAttribute('aria-disabled');
        button.disabled = false;
        label.textContent = strings.addToCart;
      }
      const buyNow = this.querySelector('[data-buy-now]');
      if (buyNow) buyNow.disabled = !variant || !variant.available;
    }

    /* Risca as opções que não formam variante disponível com as outras escolhas. */
    markUnavailable() {
      const selected = this.selectedOptions();
      this.querySelectorAll('[data-option-index]').forEach((group) => {
        const index = Number(group.dataset.optionIndex);
        group.querySelectorAll('input[type="radio"]').forEach((input) => {
          const candidate = [...selected];
          candidate[index] = input.value;
          const available = this.product.variants.some(
            (v) => v.available && v.options.every((value, i) => value === candidate[i])
          );
          input.closest('.variant-pill')?.classList.toggle('variant-pill--unavailable', !available);
        });
      });
    }

    setupGallery() {
      this.slider = this.querySelector('[data-gallery-slider]');
      this.thumbs = [...this.querySelectorAll('[data-thumb-index]')];
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.slider?.goTo(Number(thumb.dataset.thumbIndex)));
      });
      this.slider?.addEventListener('slider:change', (event) => {
        this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', i === event.detail.index ? 'true' : 'false'));
        this.thumbs[event.detail.index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });

      const zoom = this.querySelector('[data-zoom-modal]');
      if (!zoom) return;
      const openZoom = () => {
        const index = this.slider ? this.slider.current : 0;
        const slide = this.slider?.slides[index];
        const src = slide?.dataset.zoomSrc;
        if (!src) return;
        const target = zoom.querySelector('[data-zoom-target]');
        const img = document.createElement('img');
        img.src = src;
        img.alt = zoom.dataset.alt || '';
        target.replaceChildren(img);
        zoom.showModal();
      };
      this.querySelector('[data-zoom-open]')?.addEventListener('click', openZoom);
      this.slider?.addEventListener('click', (event) => {
        if (event.target.closest('.gallery__slide img')) openZoom();
      });
    }

    showMedia(mediaId) {
      if (!this.slider) return;
      const index = this.slider.slides.findIndex((slide) => Number(slide.dataset.mediaId) === mediaId);
      if (index > -1) this.slider.goTo(index);
    }
  }
  customElements.define('product-info', ProductInfo);

  /* ---------- Produtos relacionados ---------- */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (!this.dataset.url) return;
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          this.load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        if (!response.ok) return;
        const fresh = parseHTML(await response.text()).querySelector('product-recommendations');
        if (fresh && fresh.innerHTML.trim()) {
          this.innerHTML = fresh.innerHTML;
          wishlist.sync();
        }
      } catch (error) {
        /* Sem recomendações: a seção fica vazia. */
      }
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);

  /* ---------- Coleção: filtros, ordenação, colunas ---------- */

  class FacetsForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      const submit = debounce(() => this.submit(), 500);
      this.form.addEventListener('change', submit);
      this.form.addEventListener('submit', (event) => {
        event.preventDefault();
        this.submit();
      });
    }

    submit() {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(this.form)) {
        if (value !== '') params.append(key, value);
      }
      window.location.href = `${window.location.pathname}?${params.toString()}`;
    }
  }
  customElements.define('facets-form', FacetsForm);

  document.addEventListener('change', (event) => {
    const sort = event.target.closest('[data-sort-select]');
    if (!sort) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sort.value);
    url.searchParams.delete('page');
    window.location.href = url.toString();
  });

  const COLUMNS_KEY = 'jade:columns';
  function applyColumns(columns) {
    const grid = document.querySelector('[data-collection-grid]');
    if (!grid || !columns) return;
    grid.style.setProperty('--cols-desktop', columns);
    document.querySelectorAll('[data-columns]').forEach((button) => {
      button.setAttribute('aria-pressed', button.dataset.columns === String(columns) ? 'true' : 'false');
    });
  }
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-columns]');
    if (!button) return;
    localStorage.setItem(COLUMNS_KEY, button.dataset.columns);
    applyColumns(button.dataset.columns);
  });

  /* ---------- Inicialização ---------- */

  /* A gaveta do menu nasce dentro do cabeçalho fixo, que cria uma camada de empilhamento própria;
     no <body> ela fica acima da sobreposição escura. Remove cópias antigas quando o editor recarrega o cabeçalho. */
  function hoistMenuDrawer() {
    const drawers = [...document.querySelectorAll('#menu-drawer')];
    const fresh = drawers.find((drawer) => drawer.parentElement !== document.body);
    if (!fresh) return;
    drawers.forEach((drawer) => {
      if (drawer !== fresh) drawer.remove();
    });
    document.body.appendChild(fresh);
  }

  function init() {
    hoistMenuDrawer();
    wishlist.sync();
    applyColumns(localStorage.getItem(COLUMNS_KEY));

    if (document.querySelector('.mobile-nav')) document.body.classList.add('has-mobile-nav');

    const backToTop = document.querySelector('[data-back-to-top]');
    if (backToTop) {
      const toggle = () => backToTop.classList.toggle('is-visible', window.scrollY > 600);
      window.addEventListener('scroll', toggle, { passive: true });
      backToTop.addEventListener('click', () => window.scrollTo({ top: 0, behavior: 'smooth' }));
      toggle();
    }

    const reveals = document.querySelectorAll('.reveal');
    if (reveals.length && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('is-revealed');
              observer.unobserve(entry.target);
            }
          });
        },
        { rootMargin: '0px 0px -10% 0px' }
      );
      reveals.forEach((el) => observer.observe(el));
    } else {
      reveals.forEach((el) => el.classList.add('is-revealed'));
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  /* No editor de temas, seções recarregadas precisam do estado dos favoritos de novo. */
  document.addEventListener('shopify:section:load', () => {
    hoistMenuDrawer();
    wishlist.sync();
    document.querySelectorAll('.reveal').forEach((el) => el.classList.add('is-revealed'));
    if (document.querySelector('.mobile-nav')) document.body.classList.add('has-mobile-nav');
  });

  window.theme.formatMoney = formatMoney;
  window.theme.openDrawer = openDrawer;
  window.theme.closeDrawer = closeDrawer;
})();
